import { useState } from "react";
import { CreditCard, Smartphone, Heart, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import PageHeader from "@/components/PageHeader";
import visa from '../assets/visa.svg';
import mastercard from '../assets/mastercard.svg';
import amex from '../assets/amex.svg';
import mpesa from '../assets/mpesa.svg';

const presets = [25, 50, 100, 250];

const Donate = () => {
  const { toast } = useToast();
  const [amount, setAmount] = useState<number | "">(50);

  const notify = (method: string) =>
    toast({
      title: "Payments coming soon",
      description: `${method} checkout for $${amount || 0} is not yet connected. We'll enable it shortly.`,
    });

  return (
    <>
      <PageHeader
        eyebrow="Support our work"
        title="Donate to power resilience."
        subtitle="Your gift funds clinics, classrooms, water points, and climate-smart livelihoods across Horn of Africa."
      />

      <section className="section-padding">
        <div className="container-narrow grid gap-10 md:grid-cols-5">
          {/* Form */}
          <div className="md:col-span-3">
            <div className="rounded-lg border border-border bg-card p-6 md:p-8 shadow-[var(--shadow-card)]">
              <h2 className="font-serif text-2xl font-semibold text-primary">Choose an amount</h2>
              <div className="mt-5 grid grid-cols-4 gap-3">
                {presets.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(p)}
                    className={`rounded-md border px-3 py-3 text-sm font-semibold transition-colors ${
                      amount === p
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:border-primary"
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="mb-2 block text-sm font-medium text-muted-foreground">
                  Or enter a custom amount (USD)
                </label>
                <Input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Enter amount"
                />
              </div>

              <h2 className="mt-10 font-serif text-2xl font-semibold text-primary">Payment method</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose how you'd like to give. Secure checkout coming soon.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <button
                  onClick={() => notify("Card")}
                  className="group flex flex-col gap-3 rounded-md border border-border bg-background p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-[var(--shadow-elegant)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary group-hover:bg-accent group-hover:text-accent-foreground">
                      <CreditCard className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">Credit / Debit card</p>
                      <p className="text-xs text-muted-foreground">Visa · Mastercard · Amex</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-t border-border pt-3">
                    <img src={visa} alt="Visa" className="h-5 w-auto" />
                    <img src={mastercard} alt="Mastercard" className="h-5 w-auto" />
                    <img src={amex} alt="Amex" className="h-5 w-auto" />
                  </div>
                </button>

                <button
                  onClick={() => notify("M-PESA")}
                  className="group flex flex-col gap-3 rounded-md border border-border bg-background p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-[var(--shadow-elegant)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary group-hover:bg-accent group-hover:text-accent-foreground">
                      <Smartphone className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">M-PESA</p>
                      <p className="text-xs text-muted-foreground">Mobile money transfer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-t border-border pt-3">
                    <img src={mpesa} alt="M-PESA" className="h-5 w-auto" />
                  </div>
                </button>
              </div>

              <Button
                size="lg"
                className="mt-8 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => notify("Donation")}
              >
                <Heart className="mr-2 h-4 w-4" /> Donate ${amount || 0}
              </Button>

              <p className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout · Receipts available on request
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="md:col-span-2">
            <div className="rounded-lg border border-border bg-secondary p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Your impact</p>
              <ul className="mt-4 space-y-4 text-sm text-foreground">
                <li><span className="font-semibold text-primary">$25</span> — Nutrition supplements for a child for one month.</li>
                <li><span className="font-semibold text-primary">$50</span> — Clean water for a family for six months.</li>
                <li><span className="font-semibold text-primary">$100</span> — A mobile clinic visit serving 30+ patients.</li>
                <li><span className="font-semibold text-primary">$250</span> — Drought-resilient seeds for a pastoralist family.</li>
              </ul>
            </div>
            <div className="mt-6 rounded-lg border border-border bg-card p-6">
              <h3 className="font-serif text-lg font-semibold text-primary">Other ways to give</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                For wire transfers, partnerships, or institutional grants, please email{" "}
                <a href="mailto:donate@credohorn.org" className="text-primary hover:text-accent">
                  donate@credo.org
                </a>.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Donate;